import React, { useMemo, useEffect } from "react";
import { Box, Badge, Avatar, useTheme } from "@mui/material";
import CustomTypography from "../common/form/CustomTypography";
import { ShoppingCart as CartIcon } from "@mui/icons-material";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react-lite";

const PreviewHeader: React.FC = () => {
  const theme = useTheme();
  const { swagStoreStore } = useStores();
  const storeName =
    swagStoreStore.settings.storeDetails.store_name || "Swageazy";
  const logo = swagStoreStore.settings.branding?.logo;

  // Get logo image source
  const logoSource = useMemo(() => {
    if (logo && logo.length > 0) {
      const firstLogo = logo[0];
      if (firstLogo instanceof File) {
        return {
          src: URL.createObjectURL(firstLogo),
          isObjectUrl: true,
        };
      }
      if (typeof firstLogo === "string") {
        return {
          src: firstLogo,
          isObjectUrl: false,
        };
      }
    }
    return null;
  }, [logo]);

  // Cleanup object URL
  useEffect(() => {
    return () => {
      if (logoSource?.isObjectUrl) {
        URL.revokeObjectURL(logoSource.src);
      }
    };
  }, [logoSource]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.customColors.white,
        borderRadius: "8px 8px 0 0",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {logoSource ? (
          <Box
            component="img"
            src={logoSource.src}
            alt="Logo"
            sx={{
              width: 32,
              height: 32,
              objectFit: "contain",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {/* 3D Cube Logo - fallback */}
            <Box
              sx={{
                width: 24,
                height: 24,
                position: "relative",
                transform: "rotate(-15deg)",
              }}
            >
              {/* Top face */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "50%",
                  backgroundColor: theme.palette.customColors.teal,
                  borderRadius: "2px 2px 0 0",
                }}
              />
              {/* Front-left face */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "60%",
                  height: "50%",
                  backgroundColor: theme.palette.customColors.purpleLight,
                  borderRadius: "0 0 0 2px",
                }}
              />
              {/* Front-right face */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: "60%",
                  height: "50%",
                  backgroundColor: theme.palette.customColors.pink,
                  borderRadius: "0 0 2px 0",
                }}
              />
            </Box>
          </Box>
        )}
        {/* <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          <CustomTypography
            variant="heading6"
            fontWeight={700}
            sx={{ color: theme.palette.customColors.blackText }}
          >
            {storeName}
          </CustomTypography>
        </Box> */}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
        <CustomTypography
          variant="body2"
          sx={{
            color: "#000000",
            fontWeight: 400,
          }}
        >
          Home
        </CustomTypography>
        <Badge
          badgeContent={3}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: theme.palette.customColors.errorRed,
              color: theme.palette.customColors.white,
            },
          }}
        >
          <CartIcon sx={{ color: theme.palette.customColors.blackText }} />
        </Badge>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            border: `1px solid ${theme.palette.customColors.lightGray}`,
          }}
        >
          U
        </Avatar>
      </Box>
    </Box>
  );
};

export default observer(PreviewHeader);
